/**
 * SettingsScreen — account-level settings (PRD §6 + §9).
 *
 * v1.0-D wires the canonical v1.0 settings surface:
 *
 *   1. Profile form — full_name + license_number, saved as an
 *      UPDATE on public.users. The full_name feeds the v1.0-C2
 *      parent hand-off PDF header (so clinicians no longer fall
 *      back through clinic_name → email → "Your clinician").
 *   2. Sign out — the v1.0-A sign-out button, now in a sane
 *      location (Apple App Store guideline 5.1.1(iv) requires
 *      sign-out to be discoverable).
 *   3. Subscription / IAP entry points — "Upgrade to Pro" CTA
 *      (Free users) → /upgrade. "Manage Subscription" (Pro
 *      users) → native iOS subscription management.
 *   4. Privacy Policy + Terms of Service links — open the
 *      bundled docs via the history API. Navigates inside the
 *      WebView for v1.0-D, which is acceptable for App Review;
 *      a native in-app browser sheet is a future polish item.
 *
 * Conventions mirror StudentFormScreen.tsx + SessionPlannerScreen.tsx:
 *   * `: ReactElement` return type.
 *   * Tailwind via the existing class names.
 *   * `void (async () => { ... })()` for fire-and-forget promises.
 *   * Cancellation guard on every async effect.
 *   * No template literals for auth headers (use + concat).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuth } from '../lib/auth/useAuth';
import { useProAccess } from '../lib/paywall/access';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';
import { useBreakpoint } from '../lib/ui/useBreakpoint';

interface Props {
  onBack: () => void;
  onOpenPaywall: () => void;
}

interface ProfileForm {
  fullName: string;
  licenseNumber: string;
}

interface UserProfileRow {
  full_name: string | null;
  license_number: string | null;
  students_enabled: number | null;
}

const PRIVACY_POLICY_URL = '/docs/PRIVACY_POLICY.md';
const TERMS_OF_SERVICE_URL = '/docs/TERMS_OF_SERVICE.md';

export function SettingsScreen({
  onBack,
  onOpenPaywall,
}: Props): ReactElement {
  const { user, signOut } = useAuth();
  const { state } = useProAccess();
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop' || bp === 'wide';
  const [form, setForm] = useState<ProfileForm>({
    fullName: '',
    licenseNumber: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('users')
        .select('full_name, license_number, students_enabled')
        .eq('id', user?.id ?? '')
        .maybeSingle();
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setLoaded(true);
        return;
      }
      const row = (data as UserProfileRow | null) ?? null;
      setForm({
        fullName: row?.full_name ?? '',
        licenseNumber: row?.license_number ?? '',
      });
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  async function handleSaveProfile(): Promise<void> {
    if (!user) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const fullName = form.fullName.trim();
      const licenseNumber = form.licenseNumber.trim();
      const { error: uErr } = await supabase
        .from('users')
        .update({
          full_name: fullName.length > 0 ? fullName : null,
          license_number: licenseNumber.length > 0 ? licenseNumber : null,
        })
        .eq('id', user.id);
      if (uErr) {
        setError(uErr.message);
        return;
      }
      setMessage('Profile saved.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut(): Promise<void> {
    try {
      await signOut();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed.');
    }
  }

  // Keep a ref to the latest save handler so the global ⌘S / Ctrl S
  // listener (registered once) always submits the current form state
  // instead of the empty initial state captured at first render.
  const saveProfileRef = useRef(handleSaveProfile);
  useEffect(() => {
    saveProfileRef.current = handleSaveProfile;
  }, [handleSaveProfile]);

  // ⌘ S / Ctrl S to save (issue #433 acceptance criterion #7).
  // We preventDefault to avoid the browser's Save Page dialog and
  // always show feedback — even when the form has no changes —
  // so the SLP gets immediate confirmation that the shortcut
  // fired.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 's' && e.key !== 'S') return;
      e.preventDefault();
      void (async () => {
        await saveProfileRef.current();
      })();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  function openExternal(href: string): void {
    // For v1.0-D shipped from this Linux box we just navigate
    // inside the WebView. A native in-app browser sheet is a
    // future polish item.
    window.history.pushState({}, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  const isPro = state.kind === 'pro';

  if (!loaded) {
    return (
      <main className="screen">
        <p className="tagline">Loading settings…</p>
      </main>
    );
  }

  return (
    <main className="screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <div className="settings-hero">
          <BrandMonogram size={48} />
          <div>
            <h1>Settings</h1>
            <p className="tagline">
              Signed in as {user?.email ?? '(unknown)'}.
            </p>
          </div>
        </div>
      </header>

      <fieldset className="form-fieldset">
        <legend>Profile</legend>
        <p className="tagline form-hint">
          Your name shows on the parent hand-off PDF. Your license
          number is optional and is used to verify your clinical
          credentials if you upgrade to Pro.
        </p>
        <label>
          Full name
          <input
            type="text"
            value={form.fullName}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, fullName: e.target.value }))
            }
            placeholder="e.g. Casey Park, M.S., CCC-SLP"
            autoComplete="name"
          />
        </label>
        <label>
          License number
          <input
            type="text"
            value={form.licenseNumber}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                licenseNumber: e.target.value,
              }))
            }
            placeholder="e.g. CCC-SLP-12345"
            autoComplete="off"
          />
        </label>
        <div className="form-actions">
          <button
            type="button"
            className="snap-button"
            disabled={saving}
            onClick={() => void handleSaveProfile()}
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
          {isDesktop && (
            <span className="kbd-hint" aria-hidden="true">
              <kbd>⌘</kbd>
              <kbd>S</kbd>
              <span className="kbd-hint-label">to save</span>
            </span>
          )}
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>Subscription</legend>
        <p className="tagline form-hint">
          {isPro
            ? 'You\u2019re on EffectiveSLP Pro. Manage your subscription from the App Store.'
            : 'You\u2019re on the Free tier (2 students, 10 AI generations / month).'}
        </p>
        {!isPro && (
          <button
            type="button"
            className="snap-button"
            onClick={onOpenPaywall}
          >
            Upgrade to Pro
          </button>
        )}
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>Account</legend>
        <button
          type="button"
          className="link-button"
          onClick={() => void handleSignOut()}
        >
          Sign out
        </button>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>Legal</legend>
        <div className="form-row">
          <button
            type="button"
            className="link-button"
            onClick={() => openExternal(PRIVACY_POLICY_URL)}
          >
            Privacy Policy
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => openExternal(TERMS_OF_SERVICE_URL)}
          >
            Terms of Service
          </button>
        </div>
      </fieldset>

      {error && <p className="error">{error}</p>}
      {message && <p className="tagline">{message}</p>}
    </main>
  );
}
